import { Track } from "@/src/lib/tracking";
import { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { useSnapshot } from "valtio";
import { deviceDetector } from "@ospweb/sdk-ui";
import { QUERY_KEYS } from "@config/constant";
import { OspApiClientContext } from "@features/api/client/OspApiClientProvider";
import {
  MemberProduct,
  MemberProductResponse,
  PayChannel,
} from "../features/api/memberApi";
import { I18nUtil } from "@utils/i18n";
import { rootStore } from "@store/rootStore";
import {
  ProductPayCycle,
  ProductPlanStatus,
  ProductType,
} from "../components/Member/CurrentPlan/constants";
import { useMemberUser } from "./useMemberUser";
import { getDeviceParser, getDeviceType, getValue } from "utils";
import { REMOTE_CONFIG_KEY_MEMBER_LIFETIME_AND_WEEKLY_PRODUCT } from "../features/firebase/remoteConfigKeys";
import {
  CLIENT_PLATFORM,
  type ClientPlatform,
  isConfigOnPlatform,
} from "./memberPlansData";

const isMemberLifetimeAndWeeklyProductEnabled = async () => {
  try {
    const platform = getDeviceType();
    if (platform === "pc") {
      return true;
    }
    const configValue = (
      await getValue(REMOTE_CONFIG_KEY_MEMBER_LIFETIME_AND_WEEKLY_PRODUCT)
    ).asString();
    const config = JSON.parse(configValue);
    const buildNumber = config[platform];
    const deviceInfo = getDeviceParser();
    return Number(deviceInfo.buildNumber) > buildNumber;
  } catch (error) {
    return false;
  }
};

/** 当前客户端平台：浏览器为 WEB；App 内嵌页按原生系统区分（EDG-552，产品列表 `platform` 参数） */
export const resolveClientPlatform = (): ClientPlatform => {
  if (!deviceDetector.isApp()) {
    return CLIENT_PLATFORM.WEB;
  }
  return deviceDetector.isIOSApp()
    ? CLIENT_PLATFORM.IOS
    : CLIENT_PLATFORM.ANDROID;
};

export const useMemberFeature = () => {
  const { memberApi } = useContext(OspApiClientContext);
  const { locale } = useParams<{ locale: string }>();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const isApp = deviceDetector.isApp();
  const payChannel = deviceDetector.isIOSApp()
    ? PayChannel.APPLE_IAP
    : PayChannel.GOOGLE_IAP;
  const platform = resolveClientPlatform();

  const { data: memberUser } = useMemberUser();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      QUERY_KEYS.MEMBER_FEATURE,
      locale,
      isLoggedIn,
      isApp,
      payChannel,
      platform,
    ],
    enabled: !!memberApi,
    staleTime: 60 * 1000 * 10, // 缓存10 minutes
    queryFn: async () => {
      try {
        // 不传 pay_channels、传 platform：后端只返回面向当前平台的 ACTIVE 配置，顺序即产品 payment_configs 的
        // 配置顺序；前端主 / 次级渠道完全跟随该顺序（第一个为主按钮），不再自行按渠道名区分平台（EDG-552）。
        const res = await memberApi?.member?.listMemberProducts({
          language: I18nUtil.getBackgroundLanguageKey(locale),
          platform,
        });
        const resData = res?.data as MemberProductResponse;
        Track.log("info", "Member", "[trace]getMemberProducts", {
          cause: String(resData),
        });
        if (resData?.msg === "success" && !!resData?.data) {
          const enableLifetimeAndWeeklyProduct =
            await isMemberLifetimeAndWeeklyProductEnabled();
          if (enableLifetimeAndWeeklyProduct) {
            return resData?.data ?? [];
          }
          return resData?.data.filter(
            (item) =>
              ![ProductPayCycle.WEEK, ProductPayCycle.LIFETIME].includes(
                item.pay_cycle as ProductPayCycle,
              ),
          );
        }
        return [];
      } catch (error) {
        Track.error(error, {
          tag: "Member",
          msg: "[trace]getMemberProducts error",
        });
        return [];
      }
    },
    select(data: MemberProduct[]) {
      const list = Array.isArray(data) ? data : [];
      let trialList: MemberProduct[] = [];
      // 1. 先从 memberUser.available_products 中拿到所有 trial 产品对应的 product_id 列表
      const trialProductIds =
        memberUser?.available_products
          ?.filter((item: any) => item.product_type === "trial")
          ?.map((item: any) => item.product_id)
          ?.filter(Boolean) ?? [];

      // 2. 再基于总的 member 产品列表按平台和类型筛选出真正可用的试用产品
      if (trialProductIds.length > 0) {
        if (isApp) {
          // App 端：只保留当前端对应 IAP 渠道的 ACTIVE trial 产品
          trialList = list.filter(
            (item: MemberProduct) =>
              trialProductIds.includes(item.id) &&
              item.payment_configs?.some(
                (c) => c.channel === payChannel && c.status === "ACTIVE",
              ) &&
              item.type === ProductType.TRIAL &&
              item.status === ProductPlanStatus.ACTIVE,
          );
        } else {
          // Web 端：只保留有面向网页的 ACTIVE 渠道的 trial 产品
          trialList = list.filter(
            (item: MemberProduct) =>
              trialProductIds.includes(item.id) &&
              item.payment_configs?.some(
                (c) =>
                  isConfigOnPlatform(c, CLIENT_PLATFORM.WEB) &&
                  c.status === "ACTIVE",
              ) &&
              item.type === ProductType.TRIAL &&
              item.status === ProductPlanStatus.ACTIVE,
          );
        }
      }
      // 是否存在试用产品
      const isTrial = trialList.length > 0;
      const newData = list.map((item: MemberProduct) => {
        const usdPrice = item.currencies?.find(
          (currency: any) => currency.currency === "USD",
        )?.price;

        return {
          ...item,
          // 必须兜底回 item.price：没有 USD 条目的商品会被覆盖成 undefined，价格直接空掉。
          // 原生侧 (`MemberProfileApi.displayPrice`) 一直是 `currencies USD ?: price`，
          // 这里漏了 `??` 导致两端语义不一致 —— 目前线上每个商品都带 USD 条目才没暴雷。
          price: usdPrice ?? item.price,
        };
      });

      return {
        data: newData,
        isTrial,
        trialList,
      };
    },
  });

  return {
    data: data?.data || [],
    isLoading,
    isTrial: data?.isTrial,
    trialList: data?.trialList,
    refetch,
  };
};
